import type { CoreExactMutation, CoreRead } from '@exylon/generated-contract-kernel/types';

import type { PanelGeneratedHttpPort, PanelLiveScreenId } from '../foundation/runtime-port';
import { IdentityHttpError, type IdentityHttpClient } from './identity-http';
import type { IdentityRuntimeState, RuntimeUnavailableReason } from './session-state';
import { PANEL_SCREENS, screenAdmitsSession } from './panel-screens';
import type { PanelRuntimeSession } from './session-model';

/** What a screen client asks of the Identity runtime that made it. */
export interface ScreenClientHost {
  http(): IdentityHttpClient;
  epoch(): number;
  token(): string | null;
  state(): IdentityRuntimeState;
  interactionBlock(): 'offline' | 'verification' | null;
  /** Settles when the verification fence lifts, after its bound, or when the read is retired. */
  verificationSettled(signal?: AbortSignal): Promise<void>;
  identitySettling(): boolean;
  /** Waits for what Identity has in flight: an authentication, a logout, a renewal, a hydration. */
  settleIdentity(): Promise<void>;
  expireIfDue(): void;
  expireSession(session: PanelRuntimeSession): void;
  /** Going offline or ending the screen lifetime aborts the reads it tracks; the answer stops tracking. */
  trackRead(read: AbortController): () => void;
}

const isUnauthorized = (error: unknown): boolean => error instanceof IdentityHttpError && error.kind === 'unauthenticated';
const refusalKind = (reason: RuntimeUnavailableReason): Exclude<RuntimeUnavailableReason, 'unassigned'> => reason === 'unassigned' ? 'permission' : reason;
/** The principal and everything it is granted: a read answered under one digest stays true under the same digest. */
export const authorityDigest = (session: PanelRuntimeSession): string => JSON.stringify([session.canonical.user.id, session.tenant]);

/**
 * A screen's transport: bound to the authenticated identity, restaurant and current workspace grant it was made under,
 * across renewal and foreground reads, and limited to the operations of the screen's row.
 */
export function createScreenClient(screen: PanelLiveScreenId, host: ScreenClientHost): PanelGeneratedHttpPort {
  const epoch = host.epoch();
  // Screen transport is available only to a fully projected runtime session. A credential-only identity has no grants.
  const runtimeSession = (): PanelRuntimeSession | null => {
    const state = host.state();
    return state.kind === 'ready' || state.kind === 'expired' || state.kind === 'unavailable' ? state.session : null;
  };
  const bound = runtimeSession();
  const allowed = new Set<string>(PANEL_SCREENS[screen].operations);
  const assertCurrent = () => {
    const current = runtimeSession();
    const admissionMatches = current !== null && screenAdmitsSession(current, screen);
    if (epoch !== host.epoch() || !bound || !current
      || current.tenant.kind !== bound.tenant.kind || current.tenant.id !== bound.tenant.id
      || current.canonical.user.id !== bound.canonical.user.id || !admissionMatches) {
      throw new IdentityHttpError('permission');
    }
    return current;
  };
  const invoke = async <Value>(command: CoreRead | CoreExactMutation, request: (signal?: AbortSignal) => Promise<Value>,
    owner?: AbortSignal): Promise<Value> => {
    if (!allowed.has(command.operation)) throw new IdentityHttpError('invalid_request');
    const session = assertCurrent();
    // Selectors are conveniences for the canonical owner, not authority.
    // Never dispatch a different restaurant through a retained screen port.
    const restaurantSelectors = ['restaurantId' in command.params ? command.params.restaurantId : undefined,
      command.query && 'restaurant_id' in command.query ? command.query.restaurant_id : undefined,
      command.query && 'restaurantId' in command.query ? command.query.restaurantId : undefined,
      'body' in command && 'restaurant_id' in command.body ? command.body.restaurant_id : undefined];
    if (session.tenant.kind === 'restaurant' && restaurantSelectors.some((selector) => selector !== undefined && selector !== session.tenant.id)) {
      throw new IdentityHttpError('permission');
    }
    const franchiseSelectors = ['franchiseId' in command.params ? command.params.franchiseId : undefined,
      command.query && 'franchise_id' in command.query ? command.query.franchise_id : undefined,
      'body' in command && 'franchise_id' in command.body ? command.body.franchise_id : undefined];
    if (session.tenant.kind === 'franchise' && franchiseSelectors.some((selector) => selector !== undefined && selector !== session.tenant.id)) {
      throw new IdentityHttpError('permission');
    }
    const read = !('body' in command);
    // a foreground verification is not a refusal. A mutation is never
    // queued behind it (nothing is dispatched, as before); a read waits for
    // it and is then sent with the current bearer. Only a verification that
    // outlives the bound is reported, as the transient `session_verifying`.
    const verificationRefusal = () => new IdentityHttpError(read ? 'session_verifying' : 'permission');
    // A read lives until its owner retires it, the tab goes offline or this
    // principal's screen lifetime ends; the last two abort it here.
    const lifetime = read ? new AbortController() : null;
    const signal = lifetime ? owner ? AbortSignal.any([lifetime.signal, owner]) : lifetime.signal : undefined;
    const admit = async () => {
      if (host.interactionBlock() === 'offline') throw new IdentityHttpError('network');
      if (host.interactionBlock()) {
        if (!read) throw verificationRefusal();
        await host.verificationSettled(signal);
        signal?.throwIfAborted();
        if (host.interactionBlock() === 'offline') throw new IdentityHttpError('network');
        if (host.interactionBlock()) throw verificationRefusal();
      }
      await host.settleIdentity();
      signal?.throwIfAborted();
      const admitted = assertCurrent();
      host.expireIfDue();
      const state = host.state();
      if (state.kind === 'unavailable') throw new IdentityHttpError(refusalKind(state.reason));
      if (state.kind !== 'ready') throw new IdentityHttpError('unauthenticated');
      if (host.interactionBlock() === 'offline') throw new IdentityHttpError('network');
      if (host.interactionBlock()) throw verificationRefusal();
      return admitted;
    };
    let dispatchedToken: string | null | undefined;
    const untrack = lifetime ? host.trackRead(lifetime) : null;
    try {
      for (let dispatches = 1; ; dispatches += 1) {
        const dispatchedAuthority = authorityDigest(await admit());
        dispatchedToken = host.token();
        const value = await request(signal);
        // A dispatched receipt survives an offline/verification pause. A
        // different principal or revoked admission must never receive it.
        assertCurrent();
        if (!read) return value;
        // A read is not a receipt. Do not publish new screen truth while
        // session/permission verification or connectivity is unresolved.
        if (host.interactionBlock() === 'offline') throw new IdentityHttpError('network');
        // docs/decisions/2026-10-06-panel-read-runtime.md · a foreground
        // verification is not a disconnect: an answer that crossed one waits
        // for it to settle and is kept when the principal and its permission
        // digest are unchanged. Only a changed digest sends the read once more.
        const completion = host.identitySettling() ? await admit() : assertCurrent();
        const snapshot = host.state();
        if (snapshot.kind === 'unavailable') throw new IdentityHttpError(refusalKind(snapshot.reason));
        if (snapshot.kind !== 'ready') throw new IdentityHttpError('unauthenticated');
        if (authorityDigest(completion) === dispatchedAuthority) return value;
        if (dispatches > 1) throw new IdentityHttpError('session_verifying');
      }
    } catch (error) {
      assertCurrent();
      // Renewal can rotate a credential without replacing the principal's
      // screen lifetime. A late 401 for its predecessor cannot expire it.
      if (isUnauthorized(error) && dispatchedToken !== undefined && dispatchedToken === host.token()) {
        host.expireSession(assertCurrent());
      }
      throw error;
    } finally {
      untrack?.();
    }
  };
  return {
    read: (command, options) => invoke(command, (signal) => host.http().read(command, signal ? { signal } : {}), options?.signal),
    readConditional: (command, entityTag) => invoke(command, (signal) => host.http().readConditional(command, entityTag, signal ? { signal } : {})),
    mutate: (command) => invoke(command, () => host.http().mutate(command)),
  };
}

/**
 * The screen reads on the wire or waiting for admission. Going offline or ending the principal's screen lifetime aborts
 * them; nothing else does. A read met by a foreground verification waits here for its fence to lift, up to its bound.
 */
export class ScreenReadFence {
  private readonly reads = new Set<AbortController>();
  private readonly verificationWaiters = new Set<() => void>();

  constructor(private readonly block: () => 'offline' | 'verification' | null, private readonly verificationWaitMs: number) {}

  track(read: AbortController): () => void {
    this.reads.add(read);
    return () => this.reads.delete(read);
  }

  retire(reason: () => Error): void {
    const reads = [...this.reads];
    this.reads.clear();
    for (const read of reads) read.abort(reason());
  }

  releaseVerificationWaiters(): void {
    const waiters = [...this.verificationWaiters];
    this.verificationWaiters.clear();
    waiters.forEach((release) => release());
  }

  /** Settles when the verification fence lifts, after the bound, or when the
   * waiting read is retired (the caller checks its signal). Never rejects. */
  verificationSettled(signal?: AbortSignal): Promise<void> {
    if (this.block() !== 'verification' || signal?.aborted) return Promise.resolve();
    return new Promise<void>((resolve) => {
      const release = () => {
        globalThis.clearTimeout(timer);
        this.verificationWaiters.delete(release);
        signal?.removeEventListener('abort', release);
        resolve();
      };
      const timer = globalThis.setTimeout(release, this.verificationWaitMs);
      this.verificationWaiters.add(release);
      signal?.addEventListener('abort', release, { once: true });
    });
  }
}
